import { Switch, Tooltip, useDisclosure, useToast } from "@chakra-ui/react";
import { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaButton, RanaModal } from "@nens/ranalib";

import { PublicationVersionFunctionsContext } from "../../../components/store-wrapper";
import { getIsChanged } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";

export function SwitchToVersionButton({
  toVersion,
  versionNumberToSwitchTo,
}: {
  toVersion: "draft" | "published";
  versionNumberToSwitchTo: number;
}) {
  const toast = useToast();
  const intl = useIntl();

  const { setPublicationVersion } = useContext(PublicationVersionFunctionsContext);
  const switchToDraft = toVersion === "draft";

  // If there are changes and we are switching to the published version, we want to warn the user
  // that they will lose their changes in a modal.
  const hasChanges = usePublicationStore(getIsChanged);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleClick = () => {
    if (!switchToDraft && hasChanges) {
      onOpen();
    } else if (versionNumberToSwitchTo !== null) {
      setPublicationVersion(versionNumberToSwitchTo);
      toast({
        title: intl.formatMessage(
          {
            id: "publication.version_switched",
            defaultMessage: "Switched to {version} version",
          },
          { version: switchToDraft ? "draft" : "published" }
        ),
        status: "success",
      });
    }
  };

  return (
    <>
      <Tooltip
        label={
          switchToDraft ? (
            <FormattedMessage
              id="publication.switch_to_draft"
              defaultMessage="Switch to draft version"
            />
          ) : (
            <FormattedMessage
              id="publication.switch_to_published"
              defaultMessage="Switch to published version"
            />
          )
        }
      >
        <RanaButton
          leftIcon={<Switch size="sm" isChecked={switchToDraft} pointerEvents="none" />}
          variant="tertiary"
          onClick={handleClick}
        >
          <FormattedMessage id="publication.published" defaultMessage="Published" />
        </RanaButton>
      </Tooltip>
      <RanaModal
        isOpen={isOpen}
        onClose={onClose}
        buttons={[
          <RanaButton variant="tertiary" key="cancel" onClick={onClose}>
            <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
          </RanaButton>,
          <RanaButton
            key="confirm"
            variant="danger"
            onClick={() => {
              setPublicationVersion(versionNumberToSwitchTo);
              onClose();
            }}
          >
            <FormattedMessage
              id="button.discard_changes"
              defaultMessage="Discard changes and switch"
            />
          </RanaButton>,
        ]}
        topBar={
          <FormattedMessage
            id="publication.unsaved_changes_warning"
            defaultMessage="You have unsaved changes"
          />
        }
      >
        <FormattedMessage
          id="publication.unsaved_changes_warning_message"
          defaultMessage="If you switch to the published version, you will lose all unsaved changes.{lineBreak} Are you sure you want to switch?"
          values={{ lineBreak: <br /> }}
        />
      </RanaModal>
    </>
  );
}
